import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import pluralize from 'pluralize';

import useOpenModal from '../../../../../../hooks/useOpenModal';
import useCloseModal from '../../../../../../hooks/useCloseModal';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import DismissButton from '../../../../../../components/global/DismissButton';
import useAddressBookSelect from '../../../../../../apps/pro/hooks/useAddressBookSelect';
import useAccountAddressBook from '../../../../../../apps/pro/hooks/useAccountAddressBook';
import AccountAddressBookHeader from '../../../../../../apps/pro/views/AccountAddressBook/components/AccountAddressBookHeader';
import CustomControl from '../../../../../../components/global/CustomControl';
import AccountAddressBookNullState from '../../../../../../apps/pro/views/AccountAddressBook/components/AccountAddressBookNullState';
import { UPDATE_ROSTER_IDENTITY_MODAL } from '../../../../../../constants/modals';
import {
  ADDRESS_BOOOK_CSV_UPLOAD_MODAL,
  AddressBookCSVUploadModal,
} from '../../../../../../apps/pro/views/AccountAddressBook/components/AddressBookCSVUpload';
import {
  getIdentityFullName,
  getRosterIdentityMainLinkedIdentity,
  getRosterIdentitySelfiePhoto,
} from '../../../../../../util/identity';
import Button from '../../../../../../components/global/Button';
import IdentityAvatarContainer from '../../../../../../containers/global/identity/IdentityAvatarContainer';
import { AVATAR_SIZE_28 } from '../../../../../../constants/avatar-sizes';
import {
  ROSTER_IDENTITY_PURCHASE_HISTORY_MODAL,
  RosterIdentityPurchaseHistoryModal,
} from '../../../../../../apps/pro/components/RosterIdentityPurchaseHistory';
import TablePagination from '../../../../../../components/global/TablePagination';
import RosterIdentityUpdate from '../../../../../../apps/pro/components/RosterIdentityUpdate';
import { mapRosterIdentitiesToCustomPrintRecipients } from '../../../../../../apps/pro/views/AccountAddressBook/components/AccountAddressBookTable/helpers';

const propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onAddRecipients: PropTypes.func.isRequired,
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  defaultFamilyName: PropTypes.bool,
};

const AccountCardAddressBookTable = ({
  className,
  onClose,
  onAddRecipients,
  addresses,
  defaultFamilyName = false,
}) => {
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const openAlert = useOpenAlert();
  const [useFamilyName, setUseFamilyName] = useState(defaultFamilyName);
  const {
    selectedRosterIdentities,
    isRosterIdentitySelectable,
    isRosterIdentitySelected,
    onRosterIdentitySelect,
    onRosterIdentityDeselect,
    onRosterIdentityToggle,
  } = useAddressBookSelect();

  const {
    loading,
    filter,
    addressBook = [],
    addressBookTotal,
    onPageChange,
    onAddressBookReset,
    onFilterUpdate,
    // When addresses are provided, in order to properly display em, we need
    // to check all Roster IDs. This is not ideal, but at the moment, is our best bet.
  } = useAccountAddressBook({ limit: addresses?.length > 0 ? 9999 : 6 });

  useEffect(() => {
    addressBook.forEach(rosterIdentity => {
      const [
        rosterIdentityLinkedIdentity,
      ] = mapRosterIdentitiesToCustomPrintRecipients([rosterIdentity]);

      if (
        addresses.find(
          addr => addr.id === rosterIdentityLinkedIdentity?.address?.id,
        )
      ) {
        onRosterIdentitySelect(rosterIdentity);
      }
    });
  }, [addressBook, addresses]);

  const onRosterIdentitySelectAll = () => {
    addressBook.forEach(rosterIdentity =>
      onRosterIdentitySelect(rosterIdentity),
    );
  };

  const onRosterIdentityDeselectAll = () => {
    addressBook.forEach(rosterIdentity =>
      onRosterIdentityDeselect(rosterIdentity),
    );
  };

  const selectableRosterIdentities = addressBook.filter(
    isRosterIdentitySelectable,
  );

  const { page } = filter;
  const totalPages =
    addressBookTotal > 0 ? Math.ceil(addressBookTotal / filter.limit) : 1;

  const tableClasses = classNames({
    'table table-striped mb-3': true,
    'table-loading': loading,
  });

  const classes = classNames({
    'account-card-address-book-table': true,
    [className]: !!className,
  });

  return (
    <div className={classes}>
      <div className="card-element-control-header-dismiss">
        <DismissButton onClick={() => onClose()} />
      </div>
      <div className="card-element-control-header">
        <h3 className="card-element-control-header-title">
          Select your recipients
        </h3>
      </div>

      <AccountAddressBookHeader
        canSelect={selectableRosterIdentities.length > 0}
        selectable
        filter={filter}
        onFilterUpdate={onFilterUpdate}
        openModal={openModal}
        selectedRosterIdentities={selectedRosterIdentities}
        onRosterIdentityDeselect={onRosterIdentityDeselect}
        onRosterIdentitySelect={onRosterIdentitySelect}
      />

      {/* Table */}
      <div className="table-responsive horizontal-scroller">
        <table className={tableClasses}>
          <thead className="text-bold text-sm">
            <tr>
              <td>
                <CustomControl
                  id="checkbox-select-all"
                  type="checkbox"
                  labelSrOnly
                  label="Select all contacts"
                  checked={
                    selectedRosterIdentities.length ===
                    selectableRosterIdentities.length
                  }
                  onChange={() => {
                    if (
                      selectedRosterIdentities.length ===
                      selectableRosterIdentities.length
                    ) {
                      onRosterIdentityDeselectAll();
                    } else {
                      onRosterIdentitySelectAll();
                    }
                  }}
                />
              </td>
              <td className="text-left">Name</td>
              <td>Address</td>
              <td>Birthday</td>
              <td>Matched Photos</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
            </tr>
          </thead>

          <tbody>
            {addressBook.length === 0 && !loading && (
              <tr>
                <td colSpan={6}>
                  <AccountAddressBookNullState
                    onAdd={() => {
                      openModal(UPDATE_ROSTER_IDENTITY_MODAL, {
                        modalTitle: 'Create Contact',
                      });
                    }}
                    onImport={() => openModal(ADDRESS_BOOOK_CSV_UPLOAD_MODAL)}
                  />
                </td>
              </tr>
            )}

            {addressBook.map(addressBookRecord => {
              const mainLinkedIdentity = getRosterIdentityMainLinkedIdentity(
                addressBookRecord,
              );

              const selfiePhoto = getRosterIdentitySelfiePhoto(
                addressBookRecord,
              );

              const addressLine =
                mainLinkedIdentity?.addresses?.records?.[0]?.address_line_1;

              const isSelectable = isRosterIdentitySelectable(
                addressBookRecord,
              );

              return (
                <tr key={addressBookRecord.id}>
                  <td className="text-center">
                    <CustomControl
                      id={`checkbox-${addressBookRecord.id}`}
                      type="checkbox"
                      labelSrOnly
                      label="Select contact"
                      inputClassName={!isSelectable ? 'disabled' : ''}
                      checked={isRosterIdentitySelected(addressBookRecord)}
                      onChange={() => {
                        if (!isSelectable) {
                          openAlert({
                            title: `Can't select contact`,
                            // eslint-disable-next-line react/prop-types
                            message: ({ onRequestClose }) => (
                              <div>
                                <p>
                                  Please add an address for this contact before
                                  selecting it for a Waldogram.
                                </p>

                                <Button
                                  btnStyle="link"
                                  btnSize="sm"
                                  onClick={() => {
                                    onRequestClose();
                                    openModal(UPDATE_ROSTER_IDENTITY_MODAL, {
                                      rosterIdentity: addressBookRecord,
                                      modalTitle: 'Update Contact',
                                      defaultEditAddress: true,
                                    });
                                  }}
                                >
                                  Add an address
                                </Button>
                              </div>
                            ),
                            type: 'info',
                          });
                          return;
                        }

                        onRosterIdentityToggle(addressBookRecord);
                      }}
                    />
                  </td>
                  <td className="text-left">
                    <span className="d-flex align-items-center">
                      <IdentityAvatarContainer
                        size={AVATAR_SIZE_28}
                        identity={{
                          ...addressBookRecord,
                          name: addressBookRecord.first_name,
                          current_matching_photo: selfiePhoto,
                        }}
                        className="mr-1 text-sm"
                        selfieType="matching"
                      />
                      {getIdentityFullName(addressBookRecord)}
                    </span>
                  </td>
                  <td>
                    <span className="address-book-table-cell-truncate">
                      {addressLine || (
                        <Button
                          btnStyle="link"
                          btnSize="sm"
                          onClick={() =>
                            openModal(UPDATE_ROSTER_IDENTITY_MODAL, {
                              rosterIdentity: addressBookRecord,
                              modalTitle: 'Update Contact',
                              defaultEditAddress: true,
                            })
                          }
                        >
                          Add
                        </Button>
                      )}
                    </span>
                  </td>
                  <td>
                    {addressBookRecord.date_of_birth ? (
                      moment(addressBookRecord.date_of_birth).format('MM/DD')
                    ) : (
                      <Button
                        btnStyle="link"
                        btnSize="sm"
                        onClick={() =>
                          openModal(UPDATE_ROSTER_IDENTITY_MODAL, {
                            rosterIdentity: addressBookRecord,
                            modalTitle: 'Update Contact',
                          })
                        }
                      >
                        Add
                      </Button>
                    )}
                  </td>
                  <td>
                    {addressBookRecord?.photos?.matched_photos.total ?? '-'}
                  </td>
                  <td className="text-center">
                    <Button
                      onClick={event => {
                        event.stopPropagation();

                        openModal(UPDATE_ROSTER_IDENTITY_MODAL, {
                          rosterIdentity: addressBookRecord,
                          modalTitle: 'Update Contact',
                        });
                      }}
                      btnStyle="white"
                      btnSize="xs"
                      className="btn-edit-address-book-row"
                    >
                      Edit
                    </Button>
                  </td>
                  <td className="text-center">
                    <Button
                      onClick={event => {
                        event.stopPropagation();

                        openModal(ROSTER_IDENTITY_PURCHASE_HISTORY_MODAL, {
                          rosterIdentityId: addressBookRecord.id,
                          modalTitle: `Purchase history for ${getIdentityFullName(
                            addressBookRecord,
                          )}`,
                        });
                      }}
                      btnStyle="white"
                      btnSize="xs"
                      className="btn-edit-address-book-row"
                    >
                      History
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <TablePagination
          onPageChange={onPageChange}
          page={page}
          totalPages={totalPages}
          loading={loading}
        />
      )}
      <div className="account-card-address-book-table-actions">
        <CustomControl
          id="use-family-name"
          type="checkbox"
          label="Address recipients as a family"
          className="address-list-action-button address-list-item-family-button"
          labelNote={
            <>
              e.g. &ldquo;The Smith family&rdquo; instead of &ldquo;Joe
              Smith&rdquo;
            </>
          }
          checked={useFamilyName}
          onChange={() => setUseFamilyName(!useFamilyName)}
        />
        <Button
          disabled={selectedRosterIdentities.length === 0}
          onClick={() =>
            onAddRecipients(selectedRosterIdentities, useFamilyName)
          }
        >
          {selectedRosterIdentities.length > 0 ? (
            <>
              Add {selectedRosterIdentities.length}{' '}
              {pluralize('recipient', selectedRosterIdentities.length)}
            </>
          ) : (
            <>Select a recipient</>
          )}
        </Button>
      </div>

      <RosterIdentityUpdate
        onRosterIdentityCreated={() => {
          closeModal(UPDATE_ROSTER_IDENTITY_MODAL);
          onAddressBookReset();
        }}
        onRosterIdentityUpdated={() => {
          closeModal(UPDATE_ROSTER_IDENTITY_MODAL);
        }}
      />
      <RosterIdentityPurchaseHistoryModal />
      <AddressBookCSVUploadModal onSuccess={onAddressBookReset} />
    </div>
  );
};

AccountCardAddressBookTable.propTypes = propTypes;

export default AccountCardAddressBookTable;
